import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, concatMap, filter, finalize, firstValueFrom, of, take } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { VendorApplication } from 'common';
import { SharedApiService } from '../../../services/shared-api.service';

export interface CaptureSaveRequest {
    /**
     * Factory that builds the POST payload. Called *inside* the queue's concatMap, immediately
     * before the HTTP request is dispatched. This guarantees the payload reads the latest form
     * state — including any ids written by the previous save's response — instead of a stale
     * snapshot taken at the time the request was enqueued.
     */
    buildPayload: () => VendorApplication;

    /**
     * If true, the dialog will close after this save resolves successfully.
     */
    closeAfterSave?: boolean;
}

export interface CaptureSaveResult {
    request: CaptureSaveRequest;
    response: VendorApplication | null;
    error?: unknown;
}

export interface CaptureDeleteRequest {
    /** Backend patient (VendorApplicationCustomer) id to delete. */
    patientId: number;

    /** Free-form context the consumer can read back from CaptureDeleteResult. */
    context?: unknown;
}

export interface CaptureDeleteResult {
    request: CaptureDeleteRequest;
    ok: boolean;
    error?: unknown;
}

type CaptureOperation =
    | { kind: 'save'; request: CaptureSaveRequest }
    | { kind: 'delete'; request: CaptureDeleteRequest }
    | { kind: 'removeResource'; id: number; done: (ok: boolean) => void };

/**
 * Serializes Capture Dialog application saves *and* patient deletes through a single
 * `concatMap` queue. This prevents:
 *   - duplicate VendorApplication rows from concurrent autosaves racing with `id:null`, and
 *   - delete-vs-save races where a queued save would re-assert a record that the user just
 *     requested to be deleted (the authorizer / account-holder toggle).
 *
 * Lifetime: `providedIn: 'root'` so the service outlives the dialog instance. The dialog calls
 * `flush()` before closing to ensure any in-flight or queued operation completes.
 */
@Injectable({ providedIn: 'root' })
export class CaptureSaveService {

    private operations$ = new Subject<CaptureOperation>();
    private saveResults$ = new Subject<CaptureSaveResult>();
    private deleteResults$ = new Subject<CaptureDeleteResult>();

    public readonly pending$ = new BehaviorSubject<boolean>(false);
    public readonly saved$: Observable<CaptureSaveResult> = this.saveResults$.asObservable();
    public readonly deleted$: Observable<CaptureDeleteResult> = this.deleteResults$.asObservable();

    private queueDepth = 0;

    constructor(private apiService: SharedApiService) {
        this.operations$
            .pipe(
                concatMap(op => {
                    switch (op.kind) {
                        case 'save': return this.runSave(op.request);
                        case 'delete': return this.runDelete(op.request);
                        case 'removeResource': return this.runRemoveResource(op.id, op.done);
                    }
                }),
            )
            .subscribe();
    }

    public requestSave(request: CaptureSaveRequest): void {
        this.queueDepth++;
        this.operations$.next({ kind: 'save', request });
    }

    public requestDelete(request: CaptureDeleteRequest): void {
        this.queueDepth++;
        this.operations$.next({ kind: 'delete', request });
    }

    /**
     * Soft-deletes a miscellaneous document link (VendorApplicationCustomerResource row id).
     * Queued so it can't interleave with an in-flight save of the same patient.
     * Resolves with whether the backend accepted the removal.
     */
    public requestRemoveResource(id: number): Promise<boolean> {
        this.queueDepth++;
        return new Promise(done => this.operations$.next({ kind: 'removeResource', id, done }));
    }

    /**
     * Resolves once the queue is empty and no operation is in flight.
     */
    public flush(): Promise<void> {
        if (this.queueDepth === 0 && !this.pending$.value) {
            return Promise.resolve();
        }
        return firstValueFrom(
            this.pending$.pipe(filter(p => !p && this.queueDepth === 0), take(1)),
        ).then(() => undefined);
    }

    /** Backwards-compatible alias used by existing dialog code paths. */
    public get saving$(): BehaviorSubject<boolean> {
        return this.pending$;
    }

    public get isSaving(): boolean {
        return this.pending$.value;
    }

    private runSave(request: CaptureSaveRequest) {
        this.pending$.next(true);

        let payload: VendorApplication;
        try {
            payload = request.buildPayload();
        } catch (err) {
            console.error('CaptureSaveService: buildPayload threw', err);
            this.saveResults$.next({ request, response: null, error: err });
            this.decrementQueue();
            return of(null);
        }

        return this.apiService.saveApplication(payload).pipe(
            tap(response => this.saveResults$.next({ request, response })),
            catchError(err => {
                console.error('CaptureSaveService: saveApplication failed', err);
                this.saveResults$.next({ request, response: null, error: err });
                return of(null);
            }),
            finalize(() => this.decrementQueue()),
        );
    }

    private runDelete(request: CaptureDeleteRequest) {
        this.pending$.next(true);

        return this.apiService.deletePatient(request.patientId).pipe(
            tap(() => this.deleteResults$.next({ request, ok: true })),
            catchError(err => {
                console.error('CaptureSaveService: deletePatient failed', err);
                this.deleteResults$.next({ request, ok: false, error: err });
                return of(null);
            }),
            finalize(() => this.decrementQueue()),
        );
    }

    private runRemoveResource(id: number, done: (ok: boolean) => void) {
        this.pending$.next(true);

        return this.apiService.removeVendorApplicationCustomerResource(id).pipe(
            tap(() => done(true)),
            catchError(err => {
                console.error('CaptureSaveService: removeVendorApplicationCustomerResource failed', err);
                done(false);
                return of(null);
            }),
            finalize(() => this.decrementQueue()),
        );
    }

    private decrementQueue(): void {
        this.queueDepth = Math.max(0, this.queueDepth - 1);
        if (this.queueDepth === 0) {
            this.pending$.next(false);
        }
    }
}
